import React from 'react';
import { useAppUser } from '~/components';
import { useWorkPackageOwner } from '~/hooks';
import { CrewMemberAssignment, AuthPermission, AuthScope, Facility, CommodityAssignee } from '@bechtel/types';
import { some, includes, map } from 'lodash';
import { useWeldOwner } from '../use-weld-owner';

export interface BlockPermissions {
  canCreateBlock: boolean;
  canResolveBlock: boolean;
  canViewBlocks: boolean;
  canEditBlock: boolean;
}

export function useBlockPermission(
  assignedCrewMembers?: CrewMemberAssignment[],
  workPackageFacilities?: Facility[],
  crewMemberId?: string,
  weldCommodityAssignees?: CommodityAssignee[]
): BlockPermissions {
  const { hasPermissionScope, user } = useAppUser();
  const userFacilities = user && user.facilities ? user.facilities : [];
  const isOwner = useWorkPackageOwner(assignedCrewMembers, crewMemberId);
  const isWeldOwner = useWeldOwner(weldCommodityAssignees);

  const isFacilityOwner =
    !workPackageFacilities || workPackageFacilities.length == 0 || !userFacilities || userFacilities.length == 0
      ? false
      : some(workPackageFacilities, (wpf) =>
          includes(
            map(userFacilities, (userFacility) => userFacility.id),
            wpf.id
          )
        );
  const blockPermissions = React.useMemo(() => {
    const hasWorkPackagePermissionScope = (permission: AuthPermission) =>
      (isOwner && hasPermissionScope({ permission: permission, scope: AuthScope.MY_WORK_PACKAGES })) ||
      (isFacilityOwner && hasPermissionScope({ permission: permission, scope: AuthScope.MY_AREA })) ||
      hasPermissionScope({ permission: permission, scope: AuthScope.ALL_WORK_PACKAGES });
    const hasWeldPermissionScope = (permission: AuthPermission) =>
      (isWeldOwner && hasPermissionScope({ permission: permission, scope: AuthScope.MY_COMMODITIES })) ||
      hasPermissionScope({ permission: permission, scope: AuthScope.ALL_COMMODITIES });

    const canCreateBlock =
      hasWorkPackagePermissionScope(AuthPermission.CREATE_BLOCK) || hasWeldPermissionScope(AuthPermission.CREATE_BLOCK);
    const canResolveBlock =
      hasWorkPackagePermissionScope(AuthPermission.RESOLVE_BLOCK) ||
      hasWeldPermissionScope(AuthPermission.RESOLVE_BLOCK);
    const canViewBlocks = hasPermissionScope({ permission: AuthPermission.VIEW_BLOCKS, scope: AuthScope.APPLICATION });
    const canEditBlock =
      hasWorkPackagePermissionScope(AuthPermission.EDIT_BLOCK) || hasWeldPermissionScope(AuthPermission.EDIT_BLOCK);

    return { canCreateBlock, canResolveBlock, canViewBlocks, canEditBlock };
  }, [hasPermissionScope, isOwner, isWeldOwner]);

  return blockPermissions;
}
